import { adminService } from "@/api/admin/admin.service";
import type {
  TChatCategory,
  TChatMessage,
  TChatStatsOverview,
  TChatThread,
  TCreateChatCategoryRequest,
  TUpdateChatCategoryRequest,
} from "@/api/admin/admin.types";
import {
  DataGrid,
  type TColumnDef,
  type TDataGridQuery,
} from "@/components/data-grid";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Skeleton } from "@/components/ui/Skeleton";
import { Textarea } from "@/components/ui/Textarea";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/utils";
import {
  AlertTriangle,
  Eye,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";

const PAGE_SIZE = 20;

type TTab = "threads" | "categories";

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "-";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

function StatCard({ title, value }: { title: string; value: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
        <p className="mt-1 text-2xl font-bold text-foreground">{value}</p>
      </CardContent>
    </Card>
  );
}

type TCategoryEditor = {
  open: boolean;
  category: TChatCategory | null;
  sectionId: string;
  name: string;
  description: string;
  order: string;
  isActive: boolean;
};

const EMPTY_CATEGORY_EDITOR: TCategoryEditor = {
  open: false,
  category: null,
  sectionId: "",
  name: "",
  description: "",
  order: "0",
  isActive: true,
};

export function ChatModerationPage() {
  const { toast } = useToast();
  const [tab, setTab] = useState<TTab>("threads");

  // Threads grid refetch trigger.
  const [threadsReloadTick, setThreadsReloadTick] = useState(0);

  const [stats, setStats] = useState<TChatStatsOverview | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Thread drill-in
  const [activeThread, setActiveThread] = useState<TChatThread | null>(null);
  const [messages, setMessages] = useState<TChatMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);

  const [deleteThreadTarget, setDeleteThreadTarget] = useState<TChatThread | null>(null);
  const [deleteMessageTarget, setDeleteMessageTarget] = useState<TChatMessage | null>(null);
  const [isMutating, setIsMutating] = useState(false);

  // Categories
  const [categories, setCategories] = useState<TChatCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const [categoryEditor, setCategoryEditor] = useState<TCategoryEditor>(EMPTY_CATEGORY_EDITOR);
  const [isSavingCategory, setIsSavingCategory] = useState(false);
  const [deleteCategoryTarget, setDeleteCategoryTarget] = useState<TChatCategory | null>(null);

  // Backend-whitelisted sort column for admin/chat/threads: created_at.
  const threadsFetcher = async (query: TDataGridQuery) => {
    const response = await adminService.getChatThreads(
      {
        limit: query.limit,
        offset: query.offset,
        ...(query.search ? { search: query.search } : {}),
        ...(query.sort_by ? { sort_by: query.sort_by, sort_order: query.sort_order } : {}),
      },
      { signal: query.signal }
    );
    if (response.error) throw response.error;
    return response.data;
  };

  const loadStats = useCallback(async (signal: AbortSignal) => {
    setStatsLoading(true);
    const { data } = await adminService.getChatStats({ signal });
    if (signal.aborted) return;
    setStats(data?.data ?? null);
    setStatsLoading(false);
  }, []);

  const loadCategories = useCallback(async (signal?: AbortSignal) => {
    setCategoriesLoading(true);
    setCategoriesError(null);
    const { data, error: err } = await adminService.getChatCategories({ signal });
    if (signal?.aborted) return;
    if (err) {
      setCategoriesError(await extractErrorMessage(err));
    } else if (data) {
      setCategories(data.data ?? []);
    }
    setCategoriesLoading(false);
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void loadStats(ctrl.signal);
    return () => ctrl.abort();
  }, [loadStats]);

  useEffect(() => {
    if (tab === "categories" && categories.length === 0 && !categoriesError) {
      const ctrl = new AbortController();
      void loadCategories(ctrl.signal);
      return () => ctrl.abort();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const openThread = async (thread: TChatThread) => {
    setActiveThread(thread);
    setMessages([]);
    setMessagesError(null);
    setMessagesLoading(true);
    const { data, error: err } = await adminService.getChatThreadMessages(thread.id);
    if (err) {
      setMessagesError(await extractErrorMessage(err));
    } else if (data) {
      setMessages(data.data ?? []);
    }
    setMessagesLoading(false);
  };

  const handleDeleteThread = async () => {
    if (!deleteThreadTarget) return;
    setIsMutating(true);
    const { error: err } = await adminService.deleteChatThread(deleteThreadTarget.id);
    if (err) {
      toast({
        variant: "destructive",
        title: "Failed to delete thread",
        description: await extractErrorMessage(err),
      });
    } else {
      toast({ title: "Thread deleted" });
      if (activeThread?.id === deleteThreadTarget.id) setActiveThread(null);
      setDeleteThreadTarget(null);
      setThreadsReloadTick((t) => t + 1);
    }
    setIsMutating(false);
  };

  const handleDeleteMessage = async () => {
    if (!deleteMessageTarget || !activeThread) return;
    setIsMutating(true);
    const { error: err } = await adminService.deleteChatMessage(
      activeThread.id,
      deleteMessageTarget.id
    );
    if (err) {
      toast({
        variant: "destructive",
        title: "Failed to delete message",
        description: await extractErrorMessage(err),
      });
    } else {
      toast({ title: "Message deleted" });
      setMessages((prev) => prev.filter((m) => m.id !== deleteMessageTarget.id));
      setDeleteMessageTarget(null);
    }
    setIsMutating(false);
  };

  const openCreateCategory = () => setCategoryEditor({ ...EMPTY_CATEGORY_EDITOR, open: true });

  const openEditCategory = (category: TChatCategory) =>
    setCategoryEditor({
      open: true,
      category,
      sectionId: category.section_id ?? "",
      name: category.name ?? "",
      description: category.description ?? "",
      order: String(category.category_order ?? 0),
      isActive: category.is_active ?? true,
    });

  const handleSaveCategory = async () => {
    if (!categoryEditor.name.trim() || !categoryEditor.sectionId.trim()) {
      toast({ variant: "destructive", title: "Section ID and name are required" });
      return;
    }
    setIsSavingCategory(true);
    const orderNum = Number(categoryEditor.order);
    if (categoryEditor.category) {
      const payload: TUpdateChatCategoryRequest = {
        section_id: categoryEditor.sectionId.trim(),
        name: categoryEditor.name.trim(),
        description: categoryEditor.description.trim() || null,
        category_order: Number.isFinite(orderNum) ? orderNum : 0,
        is_active: categoryEditor.isActive,
      };
      const { error: err } = await adminService.updateChatCategory(
        categoryEditor.category.id,
        payload
      );
      if (err) {
        toast({
          variant: "destructive",
          title: "Failed to update category",
          description: await extractErrorMessage(err),
        });
      } else {
        toast({ title: "Category updated" });
        setCategoryEditor(EMPTY_CATEGORY_EDITOR);
        await loadCategories();
      }
    } else {
      const payload: TCreateChatCategoryRequest = {
        section_id: categoryEditor.sectionId.trim(),
        name: categoryEditor.name.trim(),
        description: categoryEditor.description.trim() || null,
        category_order: Number.isFinite(orderNum) ? orderNum : 0,
        is_active: categoryEditor.isActive,
      };
      const { error: err } = await adminService.createChatCategory(payload);
      if (err) {
        toast({
          variant: "destructive",
          title: "Failed to create category",
          description: await extractErrorMessage(err),
        });
      } else {
        toast({ title: "Category created" });
        setCategoryEditor(EMPTY_CATEGORY_EDITOR);
        await loadCategories();
      }
    }
    setIsSavingCategory(false);
  };

  const handleDeleteCategory = async () => {
    if (!deleteCategoryTarget) return;
    setIsMutating(true);
    const { error: err } = await adminService.deleteChatCategory(deleteCategoryTarget.id);
    if (err) {
      toast({
        variant: "destructive",
        title: "Failed to delete category",
        description: await extractErrorMessage(err),
      });
    } else {
      toast({ title: "Category deleted" });
      setDeleteCategoryTarget(null);
      await loadCategories();
    }
    setIsMutating(false);
  };

  const threadColumns: TColumnDef<TChatThread>[] = [
    {
      id: "title",
      header: "Title",
      cell: (th) => <span className="font-medium text-foreground">{th.title ?? "Untitled"}</span>,
    },
    { id: "company_id", header: "Company", type: "uuid", accessor: (th) => th.company_id },
    {
      id: "section_id",
      header: "Section",
      type: "uuid",
      accessor: (th) => th.section_id,
      hideable: true,
      defaultHidden: true,
    },
    {
      id: "status",
      header: "Status",
      cell: (th) => (th.status ? <Badge variant="secondary">{th.status}</Badge> : <span className="text-muted-foreground">—</span>),
    },
    { id: "created_at", header: "Created", type: "date", sortable: true, sortKey: "created_at" },
  ];

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Chat Moderation</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Review chat threads, moderate messages, and manage categories
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const ctrl = new AbortController();
              void loadStats(ctrl.signal);
              setThreadsReloadTick((t) => t + 1);
              if (tab === "categories") void loadCategories();
            }}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        {statsLoading ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        ) : stats ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <StatCard title="Total Threads" value={String(stats.total_threads ?? 0)} />
            <StatCard title="Total Messages" value={String(stats.total_messages ?? 0)} />
            <StatCard title="Active Threads" value={String(stats.active_threads ?? 0)} />
          </div>
        ) : null}

        {/* Tabs */}
        <div className="flex gap-2 border-b border-border">
          {(["threads", "categories"] as TTab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium capitalize transition-colors ${
                tab === t
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "threads" && (
          <DataGrid<TChatThread>
            getRowId={(row) => row.id}
            persistKey="admin.chat-threads"
            ariaLabel="Chat threads"
            data={{ mode: "server", fetcher: threadsFetcher, deps: [threadsReloadTick] }}
            columns={threadColumns}
            pagination={{ mode: "offset", pageSize: PAGE_SIZE, pageSizeOptions: [10, 20, 50, 100] }}
            toolbar={{
              search: true,
              refresh: true,
              columnVisibility: true,
              density: true,
              export: ["csv", "json"],
              searchPlaceholder: "Search by thread title",
            }}
            rowActions={(th) => [
              { label: "View messages", icon: <Eye className="h-4 w-4" />, onClick: () => void openThread(th) },
              {
                label: "Delete thread",
                icon: <Trash2 className="h-4 w-4" />,
                variant: "destructive",
                onClick: () => setDeleteThreadTarget(th),
              },
            ]}
            emptyState={{ title: "No threads found." }}
          />
        )}

        {tab === "categories" && (
          <>
            <div className="flex justify-end">
              <Button size="sm" onClick={openCreateCategory}>
                <Plus className="mr-2 h-4 w-4" />
                New Category
              </Button>
            </div>
            {categoriesLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full rounded-lg" />
                ))}
              </div>
            ) : categoriesError ? (
              <div className="flex items-center gap-2 rounded-lg border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                {categoriesError}
              </div>
            ) : categories.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
                <p className="text-muted-foreground">No categories found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Name</th>
                      <th className="px-4 py-3 font-semibold">Section</th>
                      <th className="px-4 py-3 font-semibold">Order</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 text-right font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {categories.map((c) => (
                      <tr key={c.id} className="hover:bg-accent/40">
                        <td className="px-4 py-3">
                          <div className="font-medium text-foreground">{c.name}</div>
                          {c.description && (
                            <div className="text-xs text-muted-foreground">{c.description}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">
                          {c.section_id}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{c.category_order ?? 0}</td>
                        <td className="px-4 py-3">
                          <Badge variant={c.is_active ? "secondary" : "outline"}>
                            {c.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditCategory(c)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeleteCategoryTarget(c)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Thread messages drill-in */}
      <Dialog
        open={!!activeThread}
        onOpenChange={(open) => {
          if (!open) setActiveThread(null);
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{activeThread?.title ?? "Thread"}</DialogTitle>
            <DialogDescription>Messages in this thread</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto">
            {messagesLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full rounded" />
              ))
            ) : messagesError ? (
              <p className="text-sm text-destructive">{messagesError}</p>
            ) : messages.length === 0 ? (
              <p className="text-sm text-muted-foreground">No messages in this thread.</p>
            ) : (
              messages.map((m) => (
                <div
                  key={m.id}
                  className="rounded-lg border border-border bg-card p-3 text-sm"
                >
                  <div className="mb-1 flex items-center gap-2">
                    {m.message_type && (
                      <Badge variant="outline" className="text-[10px]">
                        {m.message_type}
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground">{fmtDate(m.created_at)}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="ml-auto h-6 w-6"
                      onClick={() => setDeleteMessageTarget(m)}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                  <p className="whitespace-pre-wrap break-words text-foreground">{m.content}</p>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete thread confirm */}
      <Dialog
        open={!!deleteThreadTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteThreadTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Thread</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this thread? This archives it and hides it from users.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteThreadTarget(null)}
              disabled={isMutating}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleDeleteThread()}
              disabled={isMutating}
            >
              {isMutating ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting…</>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete message confirm */}
      <Dialog
        open={!!deleteMessageTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteMessageTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Message</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this message? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteMessageTarget(null)}
              disabled={isMutating}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleDeleteMessage()}
              disabled={isMutating}
            >
              {isMutating ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting…</>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category create/edit */}
      <Dialog
        open={categoryEditor.open}
        onOpenChange={(open) => {
          if (!open) setCategoryEditor(EMPTY_CATEGORY_EDITOR);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {categoryEditor.category ? "Edit Category" : "New Category"}
            </DialogTitle>
            <DialogDescription>Chat categories are tied to a section.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Section ID (UUID)
              </label>
              <Input
                value={categoryEditor.sectionId}
                onChange={(e) =>
                  setCategoryEditor((s) => ({ ...s, sectionId: e.target.value }))
                }
                placeholder="Section ID"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Name</label>
              <Input
                value={categoryEditor.name}
                onChange={(e) => setCategoryEditor((s) => ({ ...s, name: e.target.value }))}
                placeholder="Category name"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Description
              </label>
              <Textarea
                value={categoryEditor.description}
                onChange={(e) =>
                  setCategoryEditor((s) => ({ ...s, description: e.target.value }))
                }
                placeholder="Optional description"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Order</label>
              <Input
                type="number"
                min={0}
                value={categoryEditor.order}
                onChange={(e) => setCategoryEditor((s) => ({ ...s, order: e.target.value }))}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={categoryEditor.isActive}
                onChange={(e) =>
                  setCategoryEditor((s) => ({ ...s, isActive: e.target.checked }))
                }
              />
              Active
            </label>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCategoryEditor(EMPTY_CATEGORY_EDITOR)}
              disabled={isSavingCategory}
            >
              Cancel
            </Button>
            <Button onClick={() => void handleSaveCategory()} disabled={isSavingCategory}>
              {isSavingCategory ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete category confirm */}
      <Dialog
        open={!!deleteCategoryTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteCategoryTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Category</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete “{deleteCategoryTarget?.name}”?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteCategoryTarget(null)}
              disabled={isMutating}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleDeleteCategory()}
              disabled={isMutating}
            >
              {isMutating ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting…</>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
